import type { BadgeTone } from '@fas/ui';
import {
  Permissions as P,
  type ExportFormat,
  type PayableHourPolicy,
  type PayrollPeriodAction,
  type PayrollPeriodStatus,
  type PermissionCode,
  type SessionReviewAction,
} from '@fas/types';

type Label = { label: string; tone: BadgeTone };

/*
 * Phase 1E labels: payroll cut-offs (§32), the review trail (§27, §64) and exports (§33.3).
 * Status text is always shown next to any color (§55.1).
 */

export const PERIOD_STATUS: Record<PayrollPeriodStatus, Label & { hint: string }> = {
  OPEN: { label: 'Open', tone: 'info', hint: 'Attendance is still being recorded and reviewed.' },
  UNDER_REVIEW: { label: 'Under review', tone: 'warning', hint: 'Reviewers are clearing exceptions and approvals.' },
  SUBMITTED: { label: 'Finalized', tone: 'success', hint: 'HR finalized the cut-off; attendance in it is frozen until it is locked or returned to review.' },
  LOCKED: { label: 'Locked', tone: 'neutral', hint: 'The payroll-ready report is final and reproducible.' },
  PROCESSED: { label: 'Processed', tone: 'neutral', hint: 'Accounting recorded the payroll batch.' },
  REOPENED_WITH_AUTHORIZATION: { label: 'Reopened', tone: 'danger', hint: 'Only authorized adjustments are allowed; the cut-off must be finalized and locked again.' },
};

export const PERIOD_ACTION: Record<PayrollPeriodAction, string> = {
  CREATE: 'Created',
  UPDATE: 'Edited',
  START_REVIEW: 'Review started',
  APPROVE_DEPARTMENT: 'Verified sessions approved',
  FINALIZE: 'Finalized',
  RETURN_TO_REVIEW: 'Returned to review',
  LOCK: 'Locked',
  MARK_PROCESSED: 'Marked processed',
  REOPEN: 'Reopened',
};

export const REVIEW_ACTION: Record<SessionReviewAction, string> = {
  START_REVIEW: 'Taken into review',
  VERIFY: 'Verified',
  REJECT: 'Rejected',
  APPROVE: 'Approved',
  RETURN: 'Returned to review',
  ADJUST: 'Adjusted',
};

export const PAYABLE_POLICY: Record<PayableHourPolicy, string> = {
  ACTUAL_RENDERED: 'Actual rendered time (never more than verified)',
  SCHEDULED_IF_COMPLETED: 'Scheduled time when completed as scheduled',
  MIN_OF_RENDERED_AND_SCHEDULED: 'Rendered time, up to the schedule',
  VERIFIED_MINUTES: 'Verified minutes',
  MANUAL_APPROVAL: 'Entered by the approver',
};

export const EXPORT_FORMATS: Array<{ format: ExportFormat; label: string }> = [
  { format: 'xlsx', label: 'Excel workbook (.xlsx)' },
  { format: 'csv', label: 'CSV (.csv)' },
  { format: 'pdf', label: 'PDF (.pdf)' },
  { format: 'html', label: 'Printable page (.html)' },
];

/** Who may open the cut-off list and detail (the API filters data by scope). */
export const PAYROLL_READERS: PermissionCode[] = [P.PAYROLL_PERIOD_MANAGE, P.PAYROLL_REPORT_READ, P.ATTENDANCE_FINALIZE, P.ATTENDANCE_APPROVE, P.ATTENDANCE_VERIFY];

/** Who may open the verification / approval queue. */
export const REVIEW_QUEUE_READERS: PermissionCode[] = [P.ATTENDANCE_VERIFY, P.ATTENDANCE_APPROVE, P.ATTENDANCE_FINALIZE];

/** Who may open attendance analytics (own records with report.read.self). */
export const ANALYTICS_READERS: PermissionCode[] = [
  P.REPORT_READ_SELF,
  P.REPORT_READ_DEPARTMENT,
  P.ATTENDANCE_READ_DEPARTMENT,
  P.ATTENDANCE_VERIFY,
  P.ATTENDANCE_APPROVE,
  P.ATTENDANCE_FINALIZE,
  P.DASHBOARD_READ_DEPARTMENT,
];

/** 0.6667 → "66.7%". */
export function percent(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '—';
  return `${(rate * 100).toFixed(1)}%`;
}
